import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { compose } from 'redux';
import { Box } from '@nerdwallet/currency';
import withDataDependencies from '@nerdwallet/structured-content/with-data-dependencies';
import { withMarketplaceContext } from '@nerdwallet/structured-content/contexts/MarketplaceContext';

import {
  repeatable,
  stringInputSpec,
  stringTextAreaSpec,
  boolCheckboxSpec,
  dataDependencyTypePicker,
  dataDependencyIdsAdder,
} from '../../lib/editing-configs';

import Row from '../row/row';
import AwardTableSection from './award-table-section';
import withOutOfDateMessaging from '../with-out-of-date-messaging/with-out-of-date-messaging';
import { getOfferById, unpackArrayIds } from '../../lib/helpers';

import './award-table.less';

const AwardTable = ({ sections = [], columns, data }) => {
  const offers = Object.values(_.get(data, 'value', {}));
  const awardTableSections = sections.map((section) => (
    <AwardTableSection
      key={`${section.title}-${section.subtitle}`}
      offer={getOfferById(section.cardId, offers)}
      {...section}
    />
  ));

  return (
    <Box className="awards-table">
      <Row
        centeredInParent
        standardMaxWidths
        className="award-table__sections"
        columns={columns}
      >
        {awardTableSections}
      </Row>
    </Box>
  );
};

AwardTable.propTypes = {
  columns: Row.propTypes.columns,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      offer: PropTypes.object,
      cardLinkText: PropTypes.string,
      highlight: PropTypes.string,
      title: PropTypes.string,
      image: PropTypes.string,
      imageAlt: PropTypes.string,
      linkUrl: PropTypes.string,
      subtitle: PropTypes.string,
      removeMargins: PropTypes.bool,
    })
  ),
  data: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    error: PropTypes.object,
    value: PropTypes.object,
  }),
};

AwardTable.getEditingConfig = () => [
  ...Row.getEditingConfig(),
  repeatable('sections', 'Sections', 'Section', () => [
    stringInputSpec('title', 'Title'),
    stringInputSpec('highlight', 'Highlighted Word'),
    stringInputSpec('cardId', 'Card ID', {
      help: `If your desired card is to be delivered from the offer API, enter the Card ID # in this field AND in a Dependencies - Data Type product field below. Please note: if both the Card ID and Static Image URL fields are populated, the Card ID will take precedence.`,
    }),
    stringInputSpec('cardLinkText', 'Card Link Text'),
    stringInputSpec('image', 'Static Image URL', {
      help: `If your desired card is static and not delivered from the offer API, enter the URL of the card image in this field. Please note: if both the Card ID and Static Image URL fields are populated, the Card ID will take precedence.`,
    }),
    stringInputSpec('imageAlt', 'Static Image Alt Tag'),
    stringInputSpec('linkUrl', 'Static Image Link URL'),
    stringTextAreaSpec('subtitle', 'Static Subtitle/Text Area', {
      help: `Want a hyperlink? Write it like this: [Hi](www.thing.com). NW links should be relative, e.g., [Home](/home)`,
    }),
    boolCheckboxSpec('removeMargins', 'Remove section margins?'),
  ]),
  dataDependencyTypePicker,
  dataDependencyIdsAdder,
];

AwardTable.defaultProps = {
  sections: [],
  columns: '1',
  data: {},
};

AwardTable.displayName = 'AwardTable';

const dataDependencies = (props) => {
  const dataDepencyType = _.get(props, 'dataDependency.type');
  if (!dataDepencyType) return null;
  return {
    data: {
      type: _.get(props, 'dataDependency.type'),
      ids: unpackArrayIds(_.get(props, 'dataDependency.data.ids', [])),
    },
  };
};

const WrappedAwardTable = compose(
  withMarketplaceContext,
  withOutOfDateMessaging
)(AwardTable);

export default withDataDependencies(WrappedAwardTable, dataDependencies);
